import { Mailbox, Rule } from "@/lib/types";
import { MailboxItem } from "@/components/mailboxes/MailboxItem";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyMailboxState } from "@/components/mailboxes/EmptyMailboxState";
import { RuleManager } from "@/components/rules/RuleManager";
import RuleDrawer from "@/components/rules/RuleDrawer";

interface MailboxListProps {
  mailboxes: Mailbox[];
  isLoading: boolean;
  expandedMailboxes: Set<string>;
  mailboxRules: {[key: string]: Rule[]};
  isRulesLoading: {[key: string]: boolean};
  tenantId: string;
  onMailboxToggle: (mailboxId: string) => void;
  onRuleUpdated: (mailboxId: string) => Promise<void>;
  onReorderRules: (mailboxId: string, updatedRules: Rule[]) => void;
  onMailboxDeleted: () => void;
  onDeleteRule?: (rule: Rule) => Promise<boolean>;
  setIsDialogOpen: (isOpen: boolean) => void; // Add setIsDialogOpen prop
}

export function MailboxList({
  mailboxes,
  isLoading,
  expandedMailboxes,
  mailboxRules,
  isRulesLoading,
  tenantId,
  onMailboxToggle,
  onRuleUpdated,
  onReorderRules,
  onMailboxDeleted,
  onDeleteRule,
  setIsDialogOpen // Destructure setIsDialogOpen
}: MailboxListProps) {
  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }
  
  if (mailboxes.length === 0) {
    return (
      <div className="p-6">
        <EmptyMailboxState onAddClick={() => setIsDialogOpen(true)} /> {/* Use setIsDialogOpen in EmptyMailboxState */}
      </div>
    );
  }

  return (
    <div className="divide-y">
      <div className="grid grid-cols-6 border-b px-4 py-4 font-medium">
        <div className="col-span-2 pl-7">Mailbox</div>
        <div>Type</div>
        <div>Status</div>
        <div className="col-span-2">Rules</div>
      </div>

      {mailboxes.map((mailbox) => (
        <div key={mailbox.id} className="border-b last:border-b-0">
          <div className="px-4 py-4 grid grid-cols-6 gap-4 items-center hover:bg-muted/30 cursor-pointer"
               onClick={() => onMailboxToggle(mailbox.id)}>
            <MailboxItem 
              mailbox={mailbox} 
              onDelete={onMailboxDeleted}
              expandedMailboxes={expandedMailboxes}
            />
          </div>
          
          {expandedMailboxes.has(mailbox.id) && (
            <div className="px-6 py-6">
              <div className="bg-white rounded border">
                <div className="px-4 py-3 border-b bg-muted/30">
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium">Rules</h3>
                    <RuleDrawer
                      mode="create"
                      tenantId={tenantId}
                      mailboxId={mailbox.id}
                      onSuccess={() => onRuleUpdated(mailbox.id)}
                    />
                  </div>
                </div>
                
                <div>
                  {isRulesLoading[mailbox.id] ? (
                    <div className="p-4 text-center">
                      <Skeleton className="h-8 w-full mx-auto" />
                      <Skeleton className="h-8 w-full mx-auto mt-2" />
                    </div>
                  ) : (
                    <RuleManager 
                      mailboxId={mailbox.id}
                      tenantId={tenantId}
                      rules={mailboxRules[mailbox.id] || []}
                      isLoading={false}
                      onRuleUpdated={onRuleUpdated}
                      onReorder={onReorderRules}
                      onDeleteRule={onDeleteRule}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
